import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import get from 'lodash/get';
import merge from 'lodash/merge';
import isEmpty from 'lodash/isEmpty';

import { Text } from '../text/Text';
import { useComponentTheme } from '../useComponentTheme';
import type { CardMode } from '../types';

type Props = {
  children: React.Node,
  mode: CardMode,
  componentName?: 'Description' | 'Disclaimer' | 'Subtitle' | 'Servings',
  cardType: 'ListCard' | 'Card',
};

export const CardText = ({ children, componentName, mode, cardType, ...rest }: Props) => {
  const CardTheme = useComponentTheme(theme => theme.components[cardType], mode);
  const componentTheme = merge(get(CardTheme, 'Text'), get(CardTheme, componentName, 'Text'));
  const isDescription = componentName === 'Description';
  if (componentTheme.visible === false || isEmpty(children)) return null;

  return (
    <StyledText color={componentTheme.color} componentTheme={componentTheme} isHTML={isDescription} {...rest}>
      {children}
    </StyledText>
  );
};

CardText.defaultProps = {
  mode: 'default',
  cardType: 'ListCard',
};

const truncateLinesStyles = ({ theme, componentTheme }) =>
  componentTheme.truncateLines &&
  css`
    -webkit-line-clamp: ${componentTheme.truncateLines > 3 ? 4 : 3};
    display: -webkit-box;
    -webkit-box-orient: vertical;
    word-wrap: break-word;
    overflow: hidden;

    @media (${theme.breakpoint.tablet}) {
      -webkit-line-clamp: ${componentTheme.truncateLines};
    }
  `;

const StyledText = styled(Text)`
  margin-top: 8px;
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: ${({ componentTheme }) => componentTheme.fontWeight};
  ${truncateLinesStyles};

  @media (${({ theme }) => theme.breakpoint.tablet}) {
    font-size: ${({ componentTheme }) => componentTheme.fontSize};
  }

  p {
    margin: 0;
  }
`;
